import React, { useState } from "react";
import { Alert, Button, Form, InputGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useRouteLoaderData } from "react-router-dom";

import { useAppApi } from "@/App";
import { DeveloperState } from "@/const";
import { useRequest } from "@components/AppHook";
import { tmrPushEvent } from "@components/Counters";
import { DevForm, FormFieldGroup, FormFieldHelpText } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { Awesome, DevModal } from "@components/Helpers";
import { CommonCard } from "@components/Pages";
import { url_reverse } from "@components/Utils";

export default function ContactInfo({ developerId, header, headerTools }) {
    const { t } = useTranslation();
    const apiUrl = url_reverse("api:developer:contact_info", { developer_id: developerId });
    const { apiData, setApiData } = useAppApi(apiUrl);
    const onFormSubmit = useSubmit();
    const commonInfo = useRouteLoaderData("commonInfo");
    const developerCommonInfo = useRouteLoaderData("developerCommonInfo");
    const [nameChange, setNameChange] = useState(null);
    const { api_post } = useRequest();

    if (!apiData) return;

    const info_json = JSON.parse(apiData.forms_data?.main.data.info || "{}");
    const go_to_chat =
        developerCommonInfo.developer?.state === DeveloperState.APPROVED
            ? developerCommonInfo.developer.actual_chat_url
            : null;

    const snChange = (ev, sn) => {
        const info = JSON.parse(apiData.forms_data.main.data.info);
        info[sn] = ev.target.value;
        const newApiData = { ...apiData };
        newApiData.forms_data.main.data.info = JSON.stringify(info);
        setApiData(newApiData);
    };

    const openChat = (ev) => {
        ev.preventDefault();
        tmrPushEvent("goto_chat_click", "contact_info", commonInfo.auth_info.user_id);
        window.open(go_to_chat, "_blank");
    };

    return (
        <CommonCard header={header} headerTools={headerTools}>
            {apiData.name_in_draft && (
                <Alert variant="info">
                    {t("Название компании находится на проверке. Модерация займёт до 3 рабочих дней.")}
                </Alert>
            )}
            <DevModal
                show={nameChange}
                onHide={() => setNameChange(null)}
                body={
                    <>
                        <FormFieldGroup label={t("Название")} required={true}>
                            <DevForm.TextInput
                                name="name"
                                value={nameChange?.name}
                                required={true}
                                onChange={(ev) => setNameChange((prev) => ({ ...prev, name: ev.target.value }))}
                            />
                        </FormFieldGroup>
                        <FormFieldGroup label={t("Комментарий")} required={true}>
                            <DevForm.TextArea
                                name="comment"
                                value={nameChange?.comment}
                                required={true}
                                onChange={(ev) => setNameChange((prev) => ({ ...prev, comment: ev.target.value }))}
                            />
                            <FormFieldHelpText
                                help_text={t(
                                    "Опишите, почему вы хотите изменить название компании (например, ребрендинг, изменение юридического лица и т.п.)"
                                )}
                            />
                        </FormFieldGroup>
                    </>
                }
                footer={
                    <DevForm.Button
                        variant="warning"
                        name="send_name_to_moderation"
                        onClick={(ev) => {
                            api_post(ev, apiUrl, {
                                params: { ...nameChange, action: ev.target.name },
                                then: (resp) => {
                                    if (resp.status === "ok") {
                                        setApiData((prev) => ({ ...prev, name_in_draft: resp.name_in_draft }));
                                        setNameChange(null);
                                    }
                                },
                            });
                        }}
                    >
                        {t("Отправить на модерацию")}
                    </DevForm.Button>
                }
            />
            <Form onSubmit={onFormSubmit}>
                <DevForm.Field
                    prefix="main"
                    name="name"
                    append={
                        apiData.name_in_draft ? null : (
                            <Awesome type="pencil" onClick={() => setNameChange({ name: "", comment: "" })} />
                        )
                    }
                />
                <DevForm.Field prefix="main" name="login" />
                <DevForm.Field prefix="main" name="contact_email" />
                <FormFieldGroup label={t("ID кабинета")}>
                    <InputGroup>
                        <DevForm.TextInput value={developerCommonInfo.developer.id} disabled={true} />
                        <InputGroup.Append>
                            <InputGroup.Text>
                                <Awesome
                                    type="clipboard"
                                    onClick={() => navigator.clipboard.writeText(developerCommonInfo.developer.id)}
                                />
                            </InputGroup.Text>
                        </InputGroup.Append>
                    </InputGroup>
                </FormFieldGroup>
                <FormFieldGroup>
                    <DevForm.Field prefix="main" name="contact_phone" bareControl={true} />
                </FormFieldGroup>
                <FormFieldGroup>
                    <DevForm.TextInput
                        value={info_json.telegram}
                        placeholder="telegram"
                        onChange={(ev) => snChange(ev, "telegram")}
                    />
                </FormFieldGroup>
                <DevForm.Field prefix="main" name="contact_lang" />
                <DevForm.Field
                    prefix="main"
                    name="chat_max_url"
                    append={
                        <>
                            <Awesome
                                type="clipboard"
                                onClick={() => navigator.clipboard.writeText(apiData.forms_data.main.data.chat_max_url)}
                            />
                            <span style={{ width: "20px", color: "#c0bdbd" }}>|</span>
                            <Awesome
                                type="external-link"
                                onClick={() =>
                                    window.open(apiData.forms_data.main.data.chat_max_url, "_blank", "noopener")
                                }
                            />
                        </>
                    }
                />
                {developerCommonInfo.ac_logins?.map((login, index) => {
                    if (login.origin === "vk" && !login?.is_primary) {
                        return (
                            <FormFieldGroup key={`${login.login}-${index}`} label={t("Дополнительный способ входа")}>
                                <DevForm.TextInput value={login.login} disabled />
                            </FormFieldGroup>
                        );
                    }
                    return null;
                })}
                <DevForm.Button variant="primary" type="submit" name="action_form" value="main">
                    {t("Сохранить")}
                </DevForm.Button>{" "}
                {go_to_chat && (
                    <Button variant="info" onClick={openChat}>
                        {t("Перейти в чат")}
                    </Button>
                )}
            </Form>
        </CommonCard>
    );
}
